import { FC } from 'react';
import styles from './TrendChart.module.scss';

interface TrendSeries {
  name: string;
  values: number[]; // valori per giorno (ordinati cronologici)
}

export interface TrendChartData {
  title: string;
  unit?: string;
  /** Etichette asse X (es. ["14g fa", "-12g", ..., "oggi"]). Se omesso, autogen. */
  days?: string[];
  series: TrendSeries[];
  /** Linea media squadra opzionale */
  teamMean?: number;
}

const SERIES_COLORS = ['var(--c-error)', 'var(--c-warning)', 'var(--c-success)', 'var(--c-info, #2E73B8)'];

/**
 * Trend multi-serie SVG. Adattato dal mockup AI Coach.
 * Ogni serie = atleta. Asse X = giorni cronologici. Asse Y = metrica.
 */
export const TrendChart: FC<{ chart: TrendChartData }> = ({ chart }) => {
  const w = 760;
  const h = 220;
  const pad = { l: 36, r: 16, t: 14, b: 28 };

  const series = chart.series.filter((s) => s.values.length >= 2);
  if (series.length === 0) return null;

  const allValues = series.flatMap((s) => s.values);
  const min = Math.floor(Math.min(...allValues) - 1);
  const max = Math.ceil(Math.max(...allValues) + 1);
  const days = chart.days ?? series[0].values.map((_, i) => `g${i + 1}`);
  const n = days.length;

  const xs = (i: number) => pad.l + (i / (n - 1)) * (w - pad.l - pad.r);
  const ys = (v: number) => pad.t + (1 - (v - min) / (max - min)) * (h - pad.t - pad.b);

  const yTicks = 4;
  const ticks = Array.from({ length: yTicks + 1 }, (_, i) => min + ((max - min) * i) / yTicks);

  return (
    <div className={styles.card}>
      <div className={styles.title}>{chart.title}</div>
      <div className={styles.legend}>
        {series.map((s, i) => (
          <span key={s.name} className={styles.legendItem}>
            <span className={styles.legendDot} style={{ background: SERIES_COLORS[i % SERIES_COLORS.length] }} />
            {s.name}
          </span>
        ))}
        {chart.teamMean != null && (
          <span className={styles.legendItem}>
            <span className={styles.legendDash} />
            Media squadra
          </span>
        )}
      </div>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={pad.l} x2={w - pad.r} y1={ys(t)} y2={ys(t)} stroke="var(--c-divider)" strokeWidth={1} />
            <text x={pad.l - 8} y={ys(t) + 3} fontSize={10} fill="var(--c-text-2)" textAnchor="end">
              {t.toFixed(0)}
              {chart.unit ? ` ${chart.unit}` : ''}
            </text>
          </g>
        ))}
        {chart.teamMean != null && (
          <line
            x1={pad.l}
            x2={w - pad.r}
            y1={ys(chart.teamMean)}
            y2={ys(chart.teamMean)}
            stroke="var(--c-text-3)"
            strokeWidth={1.2}
            strokeDasharray="4 4"
          />
        )}
        {days.map((label, i) =>
          i % Math.max(1, Math.floor(n / 7)) === 0 || i === n - 1 ? (
            <text key={i} x={xs(i)} y={h - 8} fontSize={10} fill="var(--c-text-2)" textAnchor="middle">
              {label}
            </text>
          ) : null,
        )}
        {series.map((s, si) => {
          const path = s.values
            .map((v, i) => `${i === 0 ? 'M' : 'L'}${xs(i)},${ys(v)}`)
            .join(' ');
          const lastX = xs(s.values.length - 1);
          const lastY = ys(s.values[s.values.length - 1]);
          return (
            <g key={s.name}>
              <path
                d={path}
                stroke={SERIES_COLORS[si % SERIES_COLORS.length]}
                strokeWidth={2}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx={lastX}
                cy={lastY}
                r={3.5}
                fill={SERIES_COLORS[si % SERIES_COLORS.length]}
                stroke="#fff"
                strokeWidth={1.5}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
};
